import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Button,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  Text,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  CheckboxGroup,
  Stack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import {
  FiPlus,
  FiSettings,
  FiBarChart2,
  FiDownload,
  FiUpload,
  FiUsers, // ✅ NEW: Import icon for staff tab
} from "react-icons/fi";
import CategorySelector from "./CategorySelector";
import TaskQuantityManager from "./TaskQuantityManager";
import ProgressTracker from "./ProgressTracker";
import BudgetSummary from "./BudgetSummary";
import { projectTaskAPI } from "../../services/api";
import { manpowerAPI } from "../../services/api";
import ReportsAndAnalytics from "./ReportsAndAnalytics";
// ✅ NEW: Import staff management component
import ProjectStaffManagement from "./ProjectStaffManagement";
// ✅ NEW: Import auth context to check user role
import { useAuth } from "../../contexts/AuthContext"; // Adjust path as needed
import { useProjectPermission } from "../../hooks/useProjectPermission";

const ManpowerBudgetManager = ({ projectId, currentProject }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const toast = useToast();

  // ✅ NEW: Get current user info
  const { user } = useAuth();
  const isAdmin =
    user?.role === "company_admin" || user?.role === "super_admin";

  // ✅ Get user's role
  const { isProjectManager, isSiteEngineer } = useProjectPermission();

  // ✅ Check if user can see reports
  const canViewReports = isAdmin || isProjectManager;

  const {
    isOpen: isCategorySelectorOpen,
    onOpen: onCategorySelectorOpen,
    onClose: onCategorySelectorClose,
  } = useDisclosure();

  const {
    isOpen: isSyncModalOpen,
    onOpen: onSyncModalOpen,
    onClose: onSyncModalClose,
  } = useDisclosure();

  const [syncLoading, setSyncLoading] = useState(false);

  // Fetch project task hierarchy on load
  useEffect(() => {
    if (projectId) {
      fetchProjectTasks();
    }
  }, [projectId]);

  const fetchProjectTasks = async () => {
    setLoading(true);
    try {
      const response = await manpowerAPI.getTaskHierarchy(projectId);

      if (response.data.success) {
        setCategories(response.data.data);
        setHasInitialized(response.data.data.length > 0);

        const allTasks = response.data.data.flatMap(
          (category) =>
            category.projectTasks?.map((task) => ({
              ...task,
              categoryName: category.name,
              categoryCode: category.code,
              totalInstalledQuantity: task.totalInstalledQuantity || 0,
              quantity: task.quantity || 0,
              quantityProgress:
                task.quantity > 0
                  ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100
                  : 0,
            })) || []
        );
        setProjectTasks(allTasks);

        fetchBudgetSummary();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch project tasks",
          status: "error",
          duration: 4000,
        });
      }
    } catch (error) {
      console.error("Error fetching project tasks:", error);
      toast({
        title: "Error",
        description: "Failed to fetch project tasks",
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetSummary = async () => {
    try {
      const response = await manpowerAPI.getProjectSummary(projectId);
      console.log("Budget summary response:", response.data);

      if (response.data.success) {
        const summaryData = response.data.data.summary;
        console.log("Initial summary data:", summaryData);

        if (!summaryData.overallQuantityProgress && projectTasks.length > 0) {
          const totalQuantityProgress = projectTasks.reduce((sum, task) => {
            const taskProgress =
              task.quantity > 0
                ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100
                : 0;
            return sum + taskProgress;
          }, 0);

          summaryData.overallQuantityProgress =
            projectTasks.length > 0
              ? totalQuantityProgress / projectTasks.length
              : 0;
        }

        setBudgetSummary(summaryData);
      }
    } catch (error) {
      console.error("Error fetching budget summary:", error);
    }
  };

  const handleCategorySelection = async (selectedCategories) => {
    setLoading(true);
    try {
      const response = await manpowerAPI.initializeTasks(
        projectId,
        selectedCategories
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: `Successfully initialized ${response.data.data.length} categories`,
          status: "success",
          duration: 4000,
        });

        await fetchProjectTasks();
        onCategorySelectorClose();
      } else {
        toast({
          title: "Error",
          description:
            response.data.message || "Failed to initialize categories",
          status: "error",
          duration: 4000,
        });
      }
    } catch (error) {
      console.error("Error initializing categories:", error);
      toast({
        title: "Error",
        description: "Failed to initialize categories",
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncMasterData = async () => {
    setSyncLoading(true);
    try {
      const response = await projectTaskAPI.syncMasterData(projectId);

      if (response.data.success) {
        toast({
          title: "Success",
          description: response.data.message,
          status: "success",
          duration: 5000,
        });

        // Refresh task list
        await fetchProjectTasks();
        onSyncModalClose();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to sync master data",
          status: "error",
          duration: 4000,
        });
      }
    } catch (error) {
      console.error("Error syncing master data:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to sync master data",
        status: "error",
        duration: 4000,
      });
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="md" color="white">
            Manpower Budget & Tracking (Active Tasks)
          </Heading>
          <Text color="gray.400" fontSize="sm">
            Manage work quantities, track quantity-based progress, and monitor
            CPI
          </Text>
        </VStack>

        <HStack spacing={3}>
          {hasInitialized && isAdmin && (
            <Button
              leftIcon={<FiUpload />}
              variant="solid"
              colorScheme="green"
              onClick={onSyncModalOpen}
              size="sm"
            >
              Sync Master Data
            </Button>
          )}
          <Button
            leftIcon={<FiSettings />}
            variant="outline"
            onClick={onCategorySelectorOpen}
          >
            {hasInitialized ? "Manage Categories" : "Setup Categories"}
          </Button>
        </HStack>
      </Flex>

      {/* Budget Summary */}
      {budgetSummary && <BudgetSummary summary={budgetSummary} />}

      {!hasInitialized ? (
        /* Setup State */
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <VStack spacing={6} py={8}>
              <Text color="gray.300" fontSize="lg" textAlign="center">
                No manpower budget configured for this project
              </Text>
              <Text color="gray.500" fontSize="sm" textAlign="center" maxW="md">
                Get started by selecting the construction categories (HVAC,
                Plumbing, Electrical, etc.) that apply to this project. This
                will create your work breakdown structure with quantity-based
                progress tracking.
              </Text>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                size="lg"
                onClick={onCategorySelectorOpen}
              >
                Setup Manpower Budget
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        /* ✅ UPDATED: Main Interface with Staff Tab */
        <Tabs
          variant="enclosed"
          colorScheme="blue"
          index={activeTabIndex}
          onChange={(index) => setActiveTabIndex(index)}
        >
          <TabList bg="gray.800" borderColor="gray.700">
            <Tab
              color="gray.300"
              _selected={{ color: "white", bg: "blue.600" }}
            >
              Task Management
            </Tab>
            <Tab
              color="gray.300"
              _selected={{ color: "white", bg: "blue.600" }}
            >
              Progress Tracking
            </Tab>

            {canViewReports && (
              <Tab
                color="gray.300"
                _selected={{ color: "white", bg: "blue.600" }}
              >
                Reports
              </Tab>
            )}

            {/* ✅ NEW: Staff Tab (Admin Only) */}
            {isAdmin && (
              <Tab
                color="gray.300"
                _selected={{ color: "white", bg: "blue.600" }}
              >
                <HStack spacing={2}>
                  <FiUsers />
                  <Text>Staff</Text>
                </HStack>
              </Tab>
            )}
          </TabList>

          <TabPanels>
            {/* Task Management Tab */}
            <TabPanel px={0}>
              <TaskQuantityManager
                categories={categories}
                projectTasks={projectTasks}
                onTaskUpdate={fetchProjectTasks}
                projectId={projectId}
              />
            </TabPanel>

            {/* Progress Tracking Tab */}
            <TabPanel px={0}>
              <ProgressTracker
                projectTasks={projectTasks}
                onProgressUpdate={fetchProjectTasks}
                projectId={projectId}
              />
            </TabPanel>

            {/* Reports Tab */}
            <TabPanel px={0}>
              <ReportsAndAnalytics
                projectTasks={projectTasks}
                categories={categories}
                budgetSummary={budgetSummary}
                projectId={projectId}
                currentProject={currentProject}
              />
            </TabPanel>

            {/* ✅ NEW: Staff Management Tab (Admin Only) */}
            {isAdmin && (
              <TabPanel px={0}>
                <ProjectStaffManagement
                  projectId={projectId}
                  projectName={currentProject?.name}
                />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      )}

      {/* Category Selection Modal */}
      <Modal
        isOpen={isCategorySelectorOpen}
        onClose={onCategorySelectorClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">
            {hasInitialized
              ? "Manage Categories"
              : "Select Construction Categories"}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack align="start" spacing={3} mb={4}>
              <Text color="gray.300" fontSize="sm">
                Categories will be configured for quantity-based progress
                tracking
              </Text>
              <Text color="gray.400" fontSize="xs">
                Progress calculations will be based on installed vs. total
                quantities
              </Text>
            </VStack>
            <CategorySelector
              onSelectionComplete={handleCategorySelection}
              existingCategories={categories}
              isUpdate={hasInitialized}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Sync Master Data Confirmation Modal */}
      <Modal isOpen={isSyncModalOpen} onClose={onSyncModalClose} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">Sync Master Data</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <Alert status="info" bg="blue.900" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text color="white" fontWeight="bold" mb={1}>
                    Add New Master Data Items
                  </Text>
                  <Text color="gray.300" fontSize="sm">
                    This will scan for new activities and subtasks that were added to
                    the master data after your project was initialized.
                  </Text>
                </Box>
              </Alert>

              <VStack align="start" spacing={2} w="full">
                <Text color="gray.300" fontSize="sm" fontWeight="bold">
                  What will happen:
                </Text>
                <Text color="gray.400" fontSize="sm">
                  ✅ New activities/subtasks will be added with quantity = 0
                </Text>
                <Text color="gray.400" fontSize="sm">
                  ✅ All existing data will be preserved
                </Text>
                <Text color="gray.400" fontSize="sm">
                  ✅ No modifications to existing tasks
                </Text>
                <Text color="gray.400" fontSize="sm">
                  ✅ Includes both global and your company's custom master data
                </Text>
              </VStack>

              <Alert status="success" bg="green.900" borderRadius="md">
                <AlertIcon />
                <Text color="white" fontSize="sm">
                  Safe to use - your existing work and progress will NOT be affected
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onSyncModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleSyncMasterData}
                isLoading={syncLoading}
                loadingText="Syncing..."
              >
                Sync Now
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ManpowerBudgetManager;
